import { useEffect, useState } from "react";

import { cn } from "../lib/utils";
import { Button } from "./button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./dialog";

export interface ConfirmDialogProps {
  open: boolean;
  title?: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;
  onConfirm?: () => Promise<void> | void;
  onCancel?: () => void;
  className?: string;
  confirmClassName?: string;
  cancelClassName?: string;
  autoCloseOnConfirm?: boolean; // default true
  focusConfirm?: boolean; // default true
  hideCancel?: boolean;
}

export function ConfirmDialog({
  open,
  title = "Are you sure?",
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  loading = false,
  disabled = false,
  onOpenChange,
  onConfirm,
  onCancel,
  className,
  confirmClassName,
  cancelClassName,
  autoCloseOnConfirm = true,
  focusConfirm = true,
  hideCancel = false,
}: ConfirmDialogProps) {
  const [internalOpen, setInternalOpen] = useState(open);

  useEffect(() => setInternalOpen(open), [open]);

  function handleChange(next: boolean) {
    setInternalOpen(next);
    onOpenChange?.(next);
    if (!next) {
      onCancel?.();
    }
  }

  async function handleConfirm() {
    if (disabled || loading) return;
    try {
      await onConfirm?.();
      if (autoCloseOnConfirm) handleChange(false);
    } catch {
      /* swallow - parent should manage error UI */
    }
  }

  return (
    <Dialog open={internalOpen} onOpenChange={handleChange}>
      <DialogContent className={cn("max-w-sm", className)}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <DialogFooter className="gap-2">
          {!hideCancel && (
            <Button
              variant="outline"
              disabled={loading}
              onClick={() => handleChange(false)}
              className={cn(cancelClassName)}
            >
              {cancelLabel}
            </Button>
          )}
          <Button
            autoFocus={focusConfirm}
            variant={destructive ? "destructive" : "default"}
            disabled={disabled || loading}
            onClick={handleConfirm}
            className={cn(destructive ? "min-w-[6rem]" : "", confirmClassName)}
          >
            {loading ? "Working…" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
